/**
 * Error Handling Utilities
 * 
 * Utilities for consistent error handling across the application
 */

import { AppError, NetworkError, ApiError, PaymentError, AppErrorCode } from '@/types/error.types';

/**
 * Create an AppError from an unknown error
 */
export function createAppError(
  error: unknown,
  code?: AppErrorCode,
  context?: Record<string, any>
): AppError {
  if (error instanceof AppError) {
    return error;
  }

  const errorCode = code || AppErrorCode.UnknownError;

  if (error instanceof Error) {
    return new AppError(errorCode, error.message, context);
  }

  if (typeof error === 'string') {
    return new AppError(errorCode, error, context);
  }

  return new AppError(errorCode, 'An unknown error occurred', context);
}

/**
 * Handle API errors and convert to AppError
 */
export function handleApiError(error: unknown): AppError {
  // Already an AppError
  if (error instanceof AppError) {
    return error;
  }

  // Axios/API error
  if (error && typeof error === 'object' && 'response' in error) {
    const apiError = error as any;
    const status = apiError.response?.status;
    const message = apiError.response?.data?.message || apiError.message || 'API request failed';

    return new ApiError(message, status, apiError.response?.data);
  }

  // Network error
  if (error instanceof Error && error.message.includes('Network')) {
    return new NetworkError('Network connection failed. Please check your internet connection.');
  }

  // Generic error
  return createAppError(error, AppErrorCode.ApiError);
}

/**
 * Handle payment errors
 */
export function handlePaymentError(error: unknown): PaymentError {
  if (error instanceof PaymentError) {
    return error;
  }

  if (error instanceof AppError) {
    return new PaymentError(error.message, 'unknown', error.details);
  }

  const message = error instanceof Error ? error.message : 'Payment processing failed';
  return new PaymentError(message);
}

/**
 * Get user-friendly error message
 */
export function getUserErrorMessage(error: unknown): string {
  if (error instanceof AppError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  // Handle axios/API error objects
  if (error && typeof error === 'object') {
    const err = error as any;
    
    // Check for common error message properties
    if (err.message && typeof err.message === 'string') {
      return err.message;
    }
    
    if (err.response?.data?.detail && typeof err.response.data.detail === 'string') {
      return err.response.data.detail;
    }
    
    if (err.response?.data?.message && typeof err.response.data.message === 'string') {
      return err.response.data.message;
    }
    
    if (err.detail && typeof err.detail === 'string') {
      return err.detail;
    }
  }

  return 'An unknown error occurred';
}

/**
 * Log error to console (development) or error tracking service (production)
 */
export function logError(error: unknown, context?: Record<string, any>): void {
  if (import.meta.env.DEV) {
    console.error('[Error]', error, context);
  } else {
    // TODO: Send to error tracking service (e.g., Sentry)
    // For now, just log to console
    console.error('[Error]', error, context);
  }
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof NetworkError) {
    return true;
  }

  if (error instanceof Error) {
    return error.message.toLowerCase().includes('network') || 
           error.message.toLowerCase().includes('connection');
  }

  return false;
}

/**
 * Check if error is a validation error
 */
export function isValidationError(error: unknown): boolean {
  if (error instanceof AppError) {
    return error.code === 'VALIDATION_ERROR';
  }

  return false;
}

/**
 * Check if error is an auth error
 */
export function isAuthError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.statusCode === 401 || error.statusCode === 403;
  }

  if (error instanceof AppError) {
    return error.code === AppErrorCode.Unauthorized || error.code === AppErrorCode.Forbidden;
  }

  return false;
}

/**
 * Retry function with exponential backoff
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: {
    maxRetries?: number;
    initialDelay?: number;
    maxDelay?: number;
    shouldRetry?: (error: unknown) => boolean;
  } = {}
): Promise<T> {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    shouldRetry = (error) => isNetworkError(error),
  } = options;

  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Don't retry if this is the last attempt or if we shouldn't retry this error
      if (attempt === maxRetries || !shouldRetry(error)) {
        throw error;
      }

      // Calculate delay with exponential backoff
      const delay = Math.min(initialDelay * Math.pow(2, attempt), maxDelay);

      console.log(`[Retry] Attempt ${attempt + 1}/${maxRetries} failed. Retrying in ${delay}ms...`);

      // Wait before retrying
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}
